import * as Yup from 'yup';
import {
    countryOptions,
    genderOptions,
    localeOptions,
    sexOptions
} from '../configs/selfProfile';

export const profileValues = (user, role) => ({
    name: user.name || '',
    birth_date: user.birth_date || '',
    sex: user.sex || 'UNKNOWN',
    gender: user.gender || 'UNKNOWN',
    locale: user.locale || '',
    timezone: user.timezone || '',
    phone: role.phone || '',
    street: role.address?.street || '',
    zip: role.address?.zip || '',
    city: role.address?.city || '',
    province: role.address?.province || '',
    country: role.address?.country || ''
});

export const localToday = () => {
    const now = new Date();
    return [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
    ].join('-');
};

export const isDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(value + 'T00:00:00Z');
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const profileSchema = (initial) =>
    Yup.object({
        name: Yup.string()
            .trim()
            .required('Introduza o nome.')
            .max(80, 'Utilize até 80 caracteres.'),
        birth_date: Yup.string()
            .test(
                'date',
                'Introduza uma data válida.',
                (value) => !value || isDate(value)
            )
            .test(
                'future',
                'A data não pode estar no futuro.',
                (value) => !value || value <= localToday()
            )
            .test(
                'clear-date',
                'A data de nascimento existente não pode ser removida.',
                (value) => !initial.birth_date || Boolean(value)
            ),
        sex: Yup.string().oneOf(sexOptions.map((option) => option.value)),
        gender: Yup.string().oneOf(genderOptions.map((option) => option.value)),
        locale: Yup.string().oneOf([
            '',
            initial.locale,
            ...localeOptions.map((option) => option.value)
        ]),
        timezone: Yup.string().test(
            'timezone',
            'Escolha um fuso horário válido.',
            (value) => {
                if (!value) return !initial.timezone;
                try {
                    new Intl.DateTimeFormat('pt-PT', { timeZone: value });
                    return true;
                } catch {
                    return false;
                }
            }
        ),
        phone: Yup.string().max(20, 'Utilize até 20 caracteres.'),
        street: Yup.string().max(200, 'Utilize até 200 caracteres.'),
        zip: Yup.string().max(15, 'Utilize até 15 caracteres.'),
        city: Yup.string().max(30, 'Utilize até 30 caracteres.'),
        province: Yup.string().max(30, 'Utilize até 30 caracteres.'),
        country: Yup.string()
            .oneOf(['', initial.country, ...countryOptions.map((option) => option.value)])
            .test(
                'clear-country',
                'Selecione um país. O país existente não pode ser removido.',
                (value) => !initial.country || Boolean(value)
            )
    });

export const buildProfilePatch = (values, initial) => {
    profileSchema(initial).validateSync(values, { abortEarly: false });
    const payload = {};
    for (const key of [
        'name',
        'birth_date',
        'sex',
        'gender',
        'locale',
        'timezone',
        'phone'
    ]) {
        const value = typeof values[key] === 'string' ? values[key].trim() : values[key];
        if (value === initial[key]) continue;
        // The backend ignores null and cannot clear these fields with an empty string.
        if (['birth_date', 'locale', 'timezone'].includes(key) && !value) continue;
        payload[key] = value;
    }
    for (const key of ['street', 'zip', 'city', 'province', 'country']) {
        const value = values[key].trim();
        if (value === initial[key] || (key === 'country' && !value)) continue;
        payload.address = { ...payload.address, [key]: value };
    }
    return payload;
};

export const getSelectedProfileRole = (state) => {
    const role = state.roles?.find(
        (item) => item.uuid === state.selectedRole && !item.disabled
    );
    if (!role || (role.account?.uuid || null) !== (state.selectedClinic?.uuid || null))
        return null;
    return role;
};
export const profileContextKey = (state) =>
    JSON.stringify([state.uuid, state.selectedRole, state.selectedClinic?.uuid || null]);
export const profileUserFromState = (state) => ({
    ...state,
    birth_date: state.birthDate || ''
});
